/**
 * Auth Context Provider for Tripsby Studio
 *
 * Provides authentication state across the app.
 * Adapted from concierge-ui/src/lib/auth/context.tsx — same Auth0 flow.
 *
 * Features:
 * - Persists auth state in localStorage
 * - Auth0 authorization code flow via OrchestratorAPI
 * - Auto-loads auth state on mount
 * - Handles token expiry (checked every 60s)
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import {
  AuthUser,
  getAuthToken,
  getUser,
  setAuthToken,
  setUser as storeUser,
  clearAuthToken,
  getTokenExpiry,
} from './token'

// Auth0 configuration — same tenant and client as concierge-ui
const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN || 'auth.tripsby.ai'
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID || 'lCxzLx70tsAI5BEB1XqIalK9VcACZXQl'

// Redirect URI — ALWAYS derived from current window.location
// Critical for staging/preview deployments with dynamic URLs.
function getRedirectUri(): string {
  return `${window.location.origin}/login`
}

interface AuthContextValue {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  tokenExpiry: Date | null
  login: (token: string, user: AuthUser, expiresIn?: number) => void
  logout: () => void
  redirectToLogin: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [tokenExpiry, setTokenExpiry] = useState<Date | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Load auth state on mount
  useEffect(() => {
    const storedToken = getAuthToken()
    const storedUser = getUser()
    const storedExpiry = getTokenExpiry()

    if (storedToken && storedUser) {
      setToken(storedToken)
      setUser(storedUser)
      setTokenExpiry(storedExpiry)
    }

    setIsLoading(false)
  }, [])

  // Check token expiry every 60 seconds
  useEffect(() => {
    if (!tokenExpiry) return

    const checkExpiry = () => {
      if (Date.now() > tokenExpiry.getTime()) {
        clearAuthToken()
        setToken(null)
        setUser(null)
        setTokenExpiry(null)
      }
    }

    const interval = setInterval(checkExpiry, 60000)
    return () => clearInterval(interval)
  }, [tokenExpiry])

  const login = useCallback((newToken: string, newUser: AuthUser, expiresIn?: number) => {
    setAuthToken(newToken, expiresIn)
    storeUser(newUser)
    setToken(newToken)
    setUser(newUser)
    setTokenExpiry(getTokenExpiry())
  }, [])

  const logout = useCallback(() => {
    // Clear local storage first (synchronous)
    clearAuthToken()

    // Redirect to Auth0 logout on the CUSTOM DOMAIN (where session cookie lives)
    const returnTo = encodeURIComponent(window.location.origin)
    window.location.href = `https://${AUTH0_DOMAIN}/v2/logout?client_id=${AUTH0_CLIENT_ID}&returnTo=${returnTo}&federated`
  }, [])

  const redirectToLogin = useCallback(() => {
    const redirectUri = getRedirectUri()

    const params = new URLSearchParams({
      client_id: AUTH0_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid profile email',
    })

    window.location.href = `https://${AUTH0_DOMAIN}/authorize?${params.toString()}`
  }, [])

  const value: AuthContextValue = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    tokenExpiry,
    login,
    logout,
    redirectToLogin,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function useRequireAuth(): AuthContextValue {
  const auth = useAuth()

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      auth.redirectToLogin()
    }
  }, [auth])

  return auth
}
